import { t } from '@lingui/core/macro';
import { getLocale } from '@luminovo/frontend-utils';
import { Box, IconButton } from '@mui/material';
import { CalendarDaysIcon, XIcon } from 'lucide-react';
import * as React from 'react';

import { colorSystem } from '../../../theme';
import { Divider } from '../../Divider';
import { InputAdornment } from '../../InputAdornment';
import { TextField, TextFieldProps } from '../../TextField';
import { Calendar } from './internal/Calendar';
import { formatTriggerDate, fromIsoDay, toIsoDay } from './internal/format';
import { ALL_GRANULARITIES, type DatePickerGranularity, GranularityTabs } from './internal/GranularityTabs';
import { type DateLocale, resolveDateLocale } from './internal/localization';
import { MonthGrid, type Period, QuarterGrid, YearGrid } from './internal/PeriodGrids';
import { PickerPopover } from './internal/PickerPopover';
import { defaultQuickPicksSingle } from './internal/quickPicks';
import { QuickPresets } from './internal/QuickPresets';
import { TypeableDateInput } from './internal/TypeableDateInput';

export type PeriodAnchor = 'start' | 'end';

export interface FieldDatePickerProps extends Omit<
    TextFieldProps,
    'value' | 'onChange' | 'onBlur' | 'onFocus' | 'type' | 'inputRef'
> {
    value: string | null;
    /** Fires on every commit, together with `onCommit`, and closes the popover. */
    onChange: (value: string | null) => void;
    /** Fires right after each `onChange`; wire it to RHF's `field.onBlur` to validate. */
    onCommit?: (value: string | null) => void;
    /** ISO `yyyy-mm-dd` minimum (inclusive). */
    minDate?: string;
    /** ISO `yyyy-mm-dd` maximum (inclusive). */
    maxDate?: string;
    /** Override the active locale (otherwise resolved from Lingui). */
    locale?: DateLocale;
    /** Forwarded to the trigger TextField. */
    inputRef?: React.Ref<HTMLInputElement>;
    name?: string;
    /** Limit which granularities the user can switch to. Defaults to all. */
    granularities?: DatePickerGranularity[];
    /** Whether a picked month, quarter or year commits its start (default) or end date. */
    periodAnchor?: PeriodAnchor;
    hideQuickPresets?: boolean;
    /** Shows a clear (×) button while a value is set; on by default. */
    clearable?: boolean;
}

export const FieldDatePicker = React.forwardRef<HTMLDivElement, FieldDatePickerProps>(
    function FieldDatePicker(props, ref) {
        const {
            value,
            onChange,
            onCommit,
            minDate,
            maxDate,
            locale,
            inputRef,
            disabled,
            size,
            placeholder,
            granularities,
            periodAnchor = 'start',
            hideQuickPresets,
            clearable = true,
            ...rest
        } = props;

        const resolvedLocale: DateLocale = locale ?? resolveDateLocale(getLocale());
        const availableGranularities = granularities && granularities.length > 0 ? granularities : ALL_GRANULARITIES;
        const initialGranularity = availableGranularities.includes('day') ? 'day' : availableGranularities[0];

        const [open, setOpen] = React.useState(false);
        const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);
        const [granularity, setGranularity] = React.useState<DatePickerGranularity>(initialGranularity);
        const [calendarMonth, setCalendarMonth] = React.useState<Date>(() => (value ? fromIsoDay(value) : new Date()));

        // Re-anchors on open only, so navigating away from the value's month doesn't snap back.
        React.useEffect(() => {
            if (open) {
                setCalendarMonth(value ? fromIsoDay(value) : new Date());
                setGranularity(initialGranularity);
            }
            // We *only* want to react to `open`; everything else is captured at
            // the moment the popover opens and shouldn't trigger a re-anchor.
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [open]);

        const handleOpen = () => {
            if (!disabled) setOpen(true);
        };
        const handleClose = () => setOpen(false);

        const handleCommit = (next: string | null) => {
            onChange(next);
            onCommit?.(next);
            setOpen(false);
        };

        const handleCalendarSelect = (d: Date) => handleCommit(toIsoDay(d));
        const handlePeriodPick = (period: Period) =>
            handleCommit(toIsoDay(periodAnchor === 'end' ? period.to : period.from));

        // Recomputed on each open, so the presets stay stable while the popover is up.
        const presets = React.useMemo(
            () => defaultQuickPicksSingle(new Date()),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [open],
        );
        const handlePresetPick = (id: string) => {
            const found = presets.find((p) => p.id === id);
            if (found) handleCommit(found.value);
        };

        // Stable Dates, or MUI X DateCalendar snaps its month back to the value's after chevron navigation.
        const selectedDate = React.useMemo(() => (value ? fromIsoDay(value) : null), [value]);
        const minDateObj = React.useMemo(() => (minDate ? fromIsoDay(minDate) : undefined), [minDate]);
        const maxDateObj = React.useMemo(() => (maxDate ? fromIsoDay(maxDate) : undefined), [maxDate]);

        const triggerDisplay = formatTriggerDate(value);

        return (
            <Box ref={ref} sx={{ display: 'inline-flex', flexDirection: 'column', width: '100%' }}>
                <TextField
                    {...rest}
                    value={triggerDisplay}
                    placeholder={placeholder ?? t`Select a date`}
                    onClick={handleOpen}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                            e.preventDefault();
                            handleOpen();
                        }
                    }}
                    inputRef={inputRef}
                    size={size}
                    disabled={disabled}
                    ref={setAnchorEl}
                    slotProps={{
                        input: {
                            readOnly: true,
                            startAdornment: (
                                <InputAdornment position="start" iconSize={20}>
                                    <CalendarDaysIcon />
                                </InputAdornment>
                            ),
                            endAdornment:
                                clearable && value && !disabled ? (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            aria-label={t`Clear date`}
                                            edge="end"
                                            // Keeps the trigger from re-opening the popover after the clear.
                                            onMouseDown={(e) => e.stopPropagation()}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleCommit(null);
                                            }}
                                        >
                                            <XIcon size={20} />
                                        </IconButton>
                                    </InputAdornment>
                                ) : undefined,
                        },
                        htmlInput: {
                            style: { cursor: disabled ? 'not-allowed' : 'pointer' },
                            'aria-haspopup': 'dialog',
                            'aria-expanded': open,
                        },
                    }}
                />
                <PickerPopover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    aria-label={t`Date picker`}
                    width={320}
                >
                    <Box sx={{ padding: '8px 8px 0' }}>
                        <TypeableDateInput
                            value={value}
                            onCommit={handleCommit}
                            locale={resolvedLocale}
                            minDate={minDateObj}
                            maxDate={maxDateObj}
                        />
                    </Box>
                    <GranularityTabs value={granularity} onChange={setGranularity} options={availableGranularities} />
                    <Divider sx={{ marginTop: '8px', borderColor: colorSystem.neutral[2] }} />
                    {granularity === 'day' && (
                        <>
                            {!hideQuickPresets && (
                                <QuickPresets
                                    presets={presets.map((p) => ({ id: p.id, label: p.label }))}
                                    onPick={handlePresetPick}
                                    aria-label={t`Quick date presets`}
                                />
                            )}
                            <Calendar
                                selected={selectedDate}
                                onMonthChange={setCalendarMonth}
                                onSelect={handleCalendarSelect}
                                minDate={minDateObj}
                                maxDate={maxDateObj}
                                locale={resolvedLocale}
                            />
                        </>
                    )}
                    {granularity === 'month' && (
                        <MonthGrid
                            referenceDate={calendarMonth}
                            onReferenceDateChange={setCalendarMonth}
                            selected={selectedDate}
                            onPick={handlePeriodPick}
                            minDate={minDateObj}
                            maxDate={maxDateObj}
                            locale={resolvedLocale}
                        />
                    )}
                    {granularity === 'quarter' && (
                        <QuarterGrid
                            referenceDate={calendarMonth}
                            onReferenceDateChange={setCalendarMonth}
                            selected={selectedDate}
                            onPick={handlePeriodPick}
                            minDate={minDateObj}
                            maxDate={maxDateObj}
                            locale={resolvedLocale}
                        />
                    )}
                    {granularity === 'year' && (
                        <YearGrid
                            referenceDate={calendarMonth}
                            onReferenceDateChange={setCalendarMonth}
                            selected={selectedDate}
                            onPick={handlePeriodPick}
                            minDate={minDateObj}
                            maxDate={maxDateObj}
                            locale={resolvedLocale}
                        />
                    )}
                </PickerPopover>
            </Box>
        );
    },
);
